import {
    Action,
    type Agency,
    type Device,
    type Merchant,
    type MerchantStore
} from "../../../common/types";
import {type ConfigurationCreateRequest, type ConfigurationDataInput, type ConfigurationItemTemp} from "../types";
import {convertConfigItemToArray, replaceConfigItemByIndex} from "../helpers";
import { type FormEvent, useEffect, useState } from 'react';
import {useAddConfigurationMutation, useUpdateConfigurationMutation} from "../configuration.api-slice";
import Breadcrumb from '../../../components/Breadcrumb';
import ConfigurationItem from "./Configurationitem";
import DeleteConfiguration from '../components/DeleteConfiguration';
import { getAgencyAndMerchantIdFromOwnerEntity } from '../../../common/helpers';
import { type OwnerEntityType } from '../../../common/constants';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface IProps {
    ownerEntityType: OwnerEntityType;
    ownerEntity: Agency | Merchant | MerchantStore | Device
    configuration: ConfigurationDataInput;
    id?: string;
}

const ConfigurationBuilder: React.FC<IProps> = ({ownerEntityType, ownerEntity, configuration, id }) => {
    const [addResource] = useAddConfigurationMutation();
    const [updateResource, { isSuccess }] = useUpdateConfigurationMutation();
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [currentConfigs, setCurrentConfigs] = useState<ConfigurationItemTemp[]>(convertConfigItemToArray(configuration));

    const { t } = useTranslation();

    const toggleDeleteModal = (): void => {
        setOpenDeleteModal((prev) => !prev);
    };


    const handleAddConfigItem = (): void => {
        setCurrentConfigs(prev =>[
            ...prev,
            { key: `key-${prev.length+1}`, value: `value-${prev.length+1}` }
        ]);
    }
    const handleSubmit = async (event: FormEvent): Promise<void> => {
        event.preventDefault();

        if (currentConfigs.length === 0) {
            toast.warning('At least one configuration item is required');
            return;
        }

        const { agencyId, merchantId } = getAgencyAndMerchantIdFromOwnerEntity(ownerEntityType as unknown as string, ownerEntity);

        const configToSend = currentConfigs.reduce<Record<string, any>>((result, configItem) => {
            result[configItem.key] = configItem.value;
            return result;
        }, {});
        const configObj: ConfigurationCreateRequest = {
            config: configToSend,
            ownerEntityType: ownerEntityType as unknown as string,
            ownerEntityId: ownerEntity.id,
            agencyId,
            merchantId
        };

        if (id != null) {
            await updateResource({ id, body: configObj });
        } else {
            void addResource(configObj);
            toast('Configuration is successfully attached');
        }
    };

    const handleConfigItemChange = (action: Action, index: number, value: {key: string, value: any}): void => {

        if (action === Action.MODIFY) {
            const updateConfigs = replaceConfigItemByIndex<ConfigurationItemTemp>(currentConfigs, index, value);
            setCurrentConfigs(updateConfigs);
        }

        if (action === Action.REMOVE) {
            setCurrentConfigs((prevState) => prevState.filter((configItem) => configItem.key !== value.key));
        }
    };


    useEffect(() => {
        if (isSuccess) {
            const message = 'Configuration is successfully updated.';
            toast(message);
        }
    }, [isSuccess]);

    return (
        <div>
            <div className="pb-4">
                <h1>{t('configuration.add.title')}</h1>
                <Breadcrumb items={[{ label: t('common.adminBreadcrumbLabel') }, { label: t('configuration.add.title') }]} />
            </div>
            <div className="w-full flex justify-between items-center">
                <div className="flex w-full h-full text-2xl text-center">
                    {id !== undefined ? t('configuration.edit.title') : t('configuration.add.title')}
                </div>
                <div className='flex'>
                    {id != null && (
                        <div className="w-1/2 flex justify-end items-center">
                            <button onClick={toggleDeleteModal} className="ml-4 px-5 py-2 rounded-lg bg-red-800 text-white">
                                {t('configuration.delete.title')}
                            </button>
                        </div>
                    )}
                    <div className="w-1/2 flex justify-end items-center">
                        <button onClick={handleAddConfigItem} className="ml-4 px-5 py-2 rounded-lg bg-primary text-white">
                            {t('configuration.add.addConfig')}
                        </button>
                    </div>
                </div>

            </div>
            <div className="w-full flex flex-col justify-between items-center">
                <div className="flex flex-col p-2 w-full">
                    <div>
                        {currentConfigs.length !== 0 && currentConfigs?.map((configItem, index) => (
                            <ConfigurationItem key={index} configItemKey={configItem.key} configItemValue={configItem.value} handleChange={handleConfigItemChange} index={index} />
                        ))}

                    </div>
                    {currentConfigs.length !== 0 && <div className=''>
                        <button onClick={handleSubmit} type="submit" className="w-24 text-white mt-2 py-2 rounded-lg bg-primary">
                            Submit
                        </button>
                    </div>}
                </div>
                {id != null && <DeleteConfiguration isOpen={openDeleteModal} toggleDeleteModal={toggleDeleteModal} id={id} />}
            </div>
        </div>
    );
};

export default ConfigurationBuilder;
